import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash01, Printer, CheckCircle } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  getInvoice,
  updateInvoice,
  deleteInvoice,
  type InvoiceWithRelations,
} from "@/api/invoices";
import { getMyGarage } from "@/api/garage";
import { toast } from "sonner";
import { cx } from "@/utils/cx";
import type { GarageDto } from "@/types/dto";

const CAN_DELETE = ["admin", "owner"];
const CAN_MARK_PAID = ["owner", "manager", "receptionist"];

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState<InvoiceWithRelations | null>(null);
  const [garage, setGarage] = useState<GarageDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canDelete = CAN_DELETE.includes(user?.role ?? "");
  const canMarkPaid = CAN_MARK_PAID.includes(user?.role ?? "");

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [invoiceData, garageData] = await Promise.all([
        getInvoice(id),
        getMyGarage().catch(() => null),
      ]);
      setInvoice(invoiceData);
      setGarage(garageData);
    } catch {
      navigate("/invoices", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleTogglePaid = async () => {
    if (!id || !invoice) return;
    setSaving(true);
    try {
      const isPaid = invoice.status === "paid";
      await updateInvoice(id, {
        status: isPaid ? "pending" : "paid",
        paidAt: isPaid ? null : new Date().toISOString(),
      });
      fetchInvoice();
      toast.success(isPaid ? "Invoice marked as pending" : "Invoice marked as paid");
    } catch {
      toast.error("Failed to update invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await deleteInvoice(id);
      toast.success("Invoice deleted");
      navigate("/invoices", { replace: true });
    } catch {
      toast.error("Failed to delete invoice");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  if (loading || !invoice) {
    return (
      <PageLayout title="Loading...">
        <div className="py-12 text-center text-text-tertiary">Loading invoice...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Invoice #${invoice.invoiceNumber}`}
      subtitle={`${invoice.customer?.firstName} ${invoice.customer?.lastName}`}
      actions={
        <div className="flex items-center gap-2">
          {canMarkPaid && (
            <button
              type="button"
              disabled={saving}
              onClick={handleTogglePaid}
              className={cx(
                "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors",
                invoice.status === "paid"
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
                saving && "opacity-50 cursor-not-allowed"
              )}
            >
              <CheckCircle className="size-5" />
              {invoice.status === "paid" ? "PAID" : "PENDING"}
            </button>
          )}
          <div className="w-px h-6 bg-border-secondary" />
          <Button
            size="md"
            color="secondary"
            iconLeading={Printer}
            onClick={() => window.print()}
          >
            Print
          </Button>
          {canDelete && (
            <Button
              size="md"
              color="primary-destructive"
              iconLeading={Trash01}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-6 print:hidden">
        <Button
          size="sm"
          color="link-gray"
          iconLeading={ArrowLeft}
          onClick={() => navigate("/invoices")}
        >
          Back to Invoices
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 print:!grid-cols-2 gap-6 mb-6">
        {/* Invoice Details */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-md font-semibold text-text-primary">Invoice Details</h2>
            <span className="print:!hidden"><StatusBadge status={invoice.status} /></span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Invoice No.</span>
              <span className="text-sm text-text-primary font-medium">#{invoice.invoiceNumber}</span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Date</span>
              <span className="text-sm text-text-primary">{invoice.dueDate}</span>
            </div>
            {garage && (
              <>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-text-tertiary w-28 shrink-0">Company</span>
                  <span className="text-sm text-text-primary font-medium">{garage.name}</span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-text-tertiary w-28 shrink-0">Brand</span>
                  <span className="text-sm text-text-primary">{garage.name}</span>
                </div>
              </>
            )}
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">VAT No.</span>
              <span className="text-sm text-text-secondary">{garage?.vatNumber || "—"}</span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">VAT / Tax</span>
              <span className="text-sm text-text-primary">{invoice.subtotal > 0 ? `${((invoice.taxAmount / invoice.subtotal) * 100).toFixed(0)}%` : "—"}</span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Payment Type</span>
              <span className="text-sm text-text-secondary capitalize">{invoice.paymentMethod?.replace("_", " ") ?? "—"}</span>
            </div>
            {invoice.paidAt && (
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Paid At</span>
                <span className="text-sm text-text-primary">{new Date(invoice.paidAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Customer & Vehicle */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6">
          <h2 className="text-md font-semibold text-text-primary mb-4">Customer & Vehicle</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Customer</span>
              <span className="text-sm">
                {invoice.customer ? (
                  <Link
                    to={`/customers/${invoice.customer.id}`}
                    className="text-fg-brand-primary hover:underline"
                  >
                    {invoice.customer.firstName} {invoice.customer.lastName}
                  </Link>
                ) : "—"}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Phone</span>
              <span className="text-sm text-text-secondary">{invoice.customer?.phone ?? "—"}</span>
            </div>
            <div className="flex items-start gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Address</span>
              <span className="text-sm text-text-secondary">
                {invoice.customer?.address
                  ? [invoice.customer.address.street, invoice.customer.address.city, invoice.customer.address.state, invoice.customer.address.zip].filter(Boolean).join(", ") || "—"
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>


      {/* Line Items */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary mb-6">
        <div className="px-4 sm:px-6 py-4 border-b border-border-secondary">
          <h2 className="text-md font-semibold text-text-primary">Line Items</h2>
        </div>

        {/* Mobile cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden print:!hidden">
          {invoice.lines?.map((line) => (
            <div key={line.id} className="rounded-lg border border-border-secondary p-3">
              <p className="font-medium text-text-primary text-sm">{line.description}</p>
              <div className="mt-1 flex items-center justify-between text-sm text-text-tertiary">
                <span>{line.quantity} x {formatCurrency(line.unitPrice)}</span>
                <span className="font-medium text-text-primary">{formatCurrency(line.total)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <table className="hidden xl:table print:!table w-full text-sm">
          <thead>
            <tr className="border-b border-border-secondary bg-bg-secondary">
              <th className="px-6 py-3 text-left font-medium text-text-tertiary">Description</th>
              <th className="px-6 py-3 text-right font-medium text-text-tertiary">Qty</th>
              <th className="px-6 py-3 text-right font-medium text-text-tertiary">Unit Price</th>
              <th className="px-6 py-3 text-right font-medium text-text-tertiary">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines?.map((line) => (
              <tr key={line.id} className="border-b border-border-secondary last:border-0">
                <td className="px-6 py-4 text-text-primary">{line.description}</td>
                <td className="px-6 py-4 text-right text-text-secondary">{line.quantity}</td>
                <td className="px-6 py-4 text-right text-text-secondary">{formatCurrency(line.unitPrice)}</td>
                <td className="px-6 py-4 text-right font-medium text-text-primary">{formatCurrency(line.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6">
        <div className="flex flex-col items-end gap-2 text-sm">
          <div className="flex justify-between w-full max-w-xs">
            <span className="text-text-tertiary">Subtotal</span>
            <span className="text-text-primary">{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="flex justify-between w-full max-w-xs">
            <span className="text-text-tertiary">VAT / Tax</span>
            <span className="text-text-primary">{formatCurrency(invoice.taxAmount)}</span>
          </div>
          <div className="flex justify-between w-full max-w-xs border-t border-border-secondary pt-2">
            <span className="font-semibold text-text-primary">Grand Total</span>
            <span className="font-semibold text-text-primary text-lg">{formatCurrency(invoice.total)}</span>
          </div>
        </div>
      </div>

      {/* Signatures — print only */}
      <div className="hidden print:!grid grid-cols-2 gap-12 mt-10 px-4">
        <div>
          <p className="text-sm font-medium text-text-primary mb-16">Issued by</p>
          <div className="border-t border-gray-400 pt-2">
            <p className="text-xs text-text-tertiary">Signature / Stamp</p>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-text-primary mb-16">Received by</p>
          <div className="border-t border-gray-400 pt-2">
            <p className="text-xs text-text-tertiary">Signature</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete invoice #{invoice.invoiceNumber}? This cannot be undone.
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button size="md" color="primary-destructive" isLoading={saving} isDisabled={saving} onClick={handleDelete}>Delete</Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
